import { TableColumn, TableRow } from '@rio-cloud/rio-uikit/Table';

import { ServiceTypeName } from '#components/order/ServiceTypeName.tsx';
import { VehiclePlate } from '#components/resources/VehiclePlate.tsx';
import type { Stop } from '#domain/Stop.ts';
import type { Tour } from '#domain/Tour.ts';
import type { Vehicle } from '#domain/Vehicle.ts';

import { StopInfo } from '../shared/StopInfo.tsx';
import TourProgressBar from './TourProgressBar.tsx';
import TourState from './TourState.tsx';
import type { TourTableColumnKey } from './TourTable.tsx';

type TourTableRowProps = {
    tour: Tour & { fleetGroupName?: string };
    vehicle?: Vehicle;
    visibleColumnKeys?: TourTableColumnKey[];
};

const getStopName = (stop: Stop) => (stop.type === 'execute' ? <ServiceTypeName value={stop.name} /> : stop.name);

const defaultVisibleColumnKeys: TourTableColumnKey[] = [
    'searchName',
    'fleetGroupName',
    'licensePlate',
    'firstStopEndTarget',
    'lastStopEndTarget',
    'progressPercentage',
    'statusWeight',
];

export const TourTableRow = ({ tour, vehicle, visibleColumnKeys = defaultVisibleColumnKeys }: TourTableRowProps) => {
    return (
        <TableRow key={tour.id} {...tour}>
            {visibleColumnKeys.map(columnKey => {
                switch (columnKey) {
                    case 'searchName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span className='max-width-10pct' title={tour.searchName}>
                                    {tour.searchName}
                                </span>
                            </TableColumn>
                        );
                    case 'fleetGroupName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span className='white-space-nowrap' title={tour.fleetGroupName}>
                                    {tour.fleetGroupName}
                                </span>
                            </TableColumn>
                        );
                    case 'licensePlate':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <VehiclePlate vehicle={vehicle} />
                            </TableColumn>
                        );
                    case 'firstStopEndTarget':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                {tour.firstStop && (
                                    <StopInfo
                                        name={getStopName(tour.firstStop)}
                                        streetAddress={tour.firstStop.location.street}
                                        city={tour.firstStop.location.city}
                                        from={tour.firstStop.startTarget}
                                        to={tour.firstStop.endTarget}
                                        zipCode={tour.firstStop.location.zipCode}
                                        country={tour.firstStop.location.country}
                                    />
                                )}
                            </TableColumn>
                        );
                    case 'lastStopEndTarget':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                {tour.lastStop && (
                                    <StopInfo
                                        name={getStopName(tour.lastStop)}
                                        streetAddress={tour.lastStop.location.street}
                                        city={tour.lastStop.location.city}
                                        from={tour.lastStop.startTarget}
                                        to={tour.lastStop.endTarget}
                                        zipCode={tour.lastStop.location.zipCode}
                                        country={tour.lastStop.location.country}
                                    />
                                )}
                            </TableColumn>
                        );
                    case 'progressPercentage':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <TourProgressBar tour={tour} />
                            </TableColumn>
                        );
                    case 'statusWeight':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <TourState tour={tour} />
                            </TableColumn>
                        );
                    default:
                        return null;
                }
            })}
        </TableRow>
    );
};
